import { Injectable } from '@angular/core';
import {
    ClaimControllerService,
    ClaimResponse,
    PagedTaskSearchRequest,
    TaskControllerService,
    TaskResponse,
    TaskSearchRequest,
    UpdateTaskPriorityRequest,
    UpdateTaskStatusRequest,
    UserResponse,
} from '@core/generated/openapi';
import { BehaviorSubject, Observable } from 'rxjs';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    Validators,
} from '@angular/forms';

@Injectable({
    providedIn: 'root',
})
export class TaskService {
    private toDosSubject: BehaviorSubject<TaskResponse[]> = new BehaviorSubject<
        TaskResponse[]
    >([]);
    public toDos$ = this.toDosSubject.asObservable();

    constructor(
        private service: TaskControllerService,
        private formBuilder: FormBuilder,
        private claimService: ClaimControllerService,
    ) {}

    setToDos(toDos: TaskResponse[]) {
        this.toDosSubject.next(toDos);
    }

    getToDos() {
        return this.toDos$;
    }

    findMyTasks(search: TaskSearchRequest): Observable<TaskResponse[]> {
        console.log('request', search);
        return this.service.findMyTasks(search);
    }

    searchTasks(search: TaskSearchRequest) {
        return this.service.findTasksByGeneralSearch(search);
    }

    searchTasksPaged(search: PagedTaskSearchRequest) {
        return this.service.findAllTasksByPagedGeneralSearch(search);
    }

    getCreateTaskForm(user?: UserResponse): FormGroup {
        let formGroup = this.formBuilder.group({
            description: new FormControl(null, {
                validators: [Validators.required, Validators.maxLength(2000)],
                updateOn: 'change',
            }),
            dueDate: new FormControl(null, {
                validators: [Validators.required],
                updateOn: 'change',
            }),
            status: new FormControl(TaskResponse.StatusEnum.Active),
            priority: new FormControl(TaskResponse.PriorityEnum.Normal, {
                validators: [Validators.required],
                updateOn: 'change',
            }),
            ownerId: new FormControl(user?.id, {
                validators: [Validators.required],
                updateOn: 'change',
            }),
            worklistModule: new FormControl(null),
        });
        return formGroup;
    }

    getEditTaskForm(task: TaskResponse): FormGroup {
        let formGroup = this.formBuilder.group({
            id: new FormControl(task.id),
            description: new FormControl(task.description, {
                validators: [Validators.required, Validators.maxLength(2000)],
                updateOn: 'change',
            }),
            dueDate: new FormControl(task.dueDate, {
                validators: [Validators.required],
                updateOn: 'change',
            }),
            status: new FormControl(task.status, { updateOn: 'change' }),
            priority: new FormControl(task.priority, {
                validators: [Validators.required],
                updateOn: 'change',
            }),
            ownerId: new FormControl(task.owner.id, {
                validators: [Validators.required],
                updateOn: 'change',
            }),
            worklistModule: new FormControl(task.worklistModule, {
                updateOn: 'change',
            }),
        });
        return formGroup;
    }

    createTask(formGroup: FormGroup): Observable<TaskResponse> {
        let request = formGroup.getRawValue();
        return this.service.createTask(request);
    }

    updateTask(formGroup: FormGroup): Observable<TaskResponse> {
        let request = formGroup.getRawValue();
        request.newOwnerId = request.ownerId;
        delete request.ownerId;
        return this.service.updateTask(request);
    }

    markUnread(id: number) {
        return this.service.markTaskAsUnread({ id: id });
    }

    markRead(id: number) {
        return this.service.markTaskAsRead(id);
    }

    complete(id: number) {
        return this.updateTaskStatus(
            id,
            UpdateTaskStatusRequest.StatusEnum.Complete,
        );
    }

    activate(id: number) {
        return this.updateTaskStatus(
            id,
            UpdateTaskStatusRequest.StatusEnum.Active,
        );
    }

    updateTaskStatus(
        id: number,
        newStatus: UpdateTaskStatusRequest.StatusEnum,
    ) {
        let request: UpdateTaskStatusRequest = {
            status: newStatus,
            id: id,
        };
        return this.service.updateTaskStatus(request);
    }

    completeTask(id: number) {
        let request = { id: id };
        return this.service.completeTask(request);
    }

    deleteTask(id: number) {
        let request = { id: id };
        return this.service.deleteTask(request);
    }

    updatePriority(
        id: number,
        newPriority: UpdateTaskPriorityRequest.PriorityEnum,
    ) {
        let request: UpdateTaskPriorityRequest = {
            priority: newPriority,
            id: id,
        };
        return this.service.updateTaskPriority(request);
    }

    findClaimToDos(claim: ClaimResponse) {
        let request: TaskSearchRequest = {
            claimId: claim.id,
        };
        console.log('ClaimId Request', request);
        return this.service.findTasksByGeneralSearch(request);
    }

    addClaimToDo(formGroup: FormGroup, claim: ClaimResponse) {
        let request = {
            description: formGroup.controls['description'].value,
            priority: formGroup.controls['priority'].value,
            dueDate: formGroup.controls['dueDate'].value,
            ownerId: formGroup.controls['ownerId'].value,
            claimId: claim.id,
        };
        return this.claimService.createClaimTask(request);
    }

    searchOwnerOrCreatorToDos(search: PagedTaskSearchRequest) {
        return this.service.findAllTodosForCreatorOrOwner(search);
    }
}
